import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyPolicyModal({
  isOpen,
  onClose
}: PrivacyPolicyModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  return <AnimatePresence data-id="element-157">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-158">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" aria-hidden="true" data-id="element-159" />

          {/* Modal Content */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-160">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white sticky top-0 z-10" data-id="element-161">
              <h2 id="modal-title" className="text-xl font-bold text-slate-900" data-id="element-162">
                Política de Privacidad y Protección de Datos
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors" aria-label="Cerrar" data-id="element-163">
                <X size={20} data-id="element-164" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="overflow-y-auto p-6 md:p-8 text-slate-600 space-y-8" data-id="element-165">
              <section data-id="element-166">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-167">
                  1. INTRODUCCIÓN
                </h3>
                <p className="leading-relaxed" data-id="element-168">
                  En TicketMaestro valoramos y respetamos la privacidad de
                  nuestros usuarios. Esta Política de Privacidad describe cómo
                  recopilamos, utilizamos, almacenamos y protegemos los datos
                  personales que usted nos proporciona al utilizar nuestros
                  sitios, APPs, terminales de autoatención u otros sistemas o
                  canales de comunicación.
                </p>
              </section>

              <section data-id="element-169">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-170">
                  2. RESPONSABLE DEL TRATAMIENTO
                </h3>
                <ul className="space-y-1 list-disc pl-5" data-id="element-171">
                  <li data-id="element-172">
                    <strong className="text-slate-800" data-id="element-173">
                      Nombre de la entidad:
                    </strong>{' '}
                    TicketMaestro SAS
                  </li>
                  <li data-id="element-174">
                    <strong className="text-slate-800" data-id="element-175">Dirección Legal:</strong>{' '}
                    Edificio The Point, Oficina 1803, Puerto Santa Ana,
                    Guayaquil, Ecuador.
                  </li>
                  <li data-id="element-176">
                    <strong className="text-slate-800" data-id="element-177">
                      Correo electrónico de contacto:
                    </strong>{' '}
                    contacto@ticketmaestro.net.
                  </li>
                </ul>
              </section>

              <section data-id="element-178">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-179">
                  3. DATOS PERSONALES QUE RECOPILAMOS
                </h3>
                <p className="mb-3" data-id="element-180">
                  Podemos recopilar los siguientes datos personales cuando
                  navegas o interactúas con nuestro sitio web u otros canales:
                </p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-181">
                  <li data-id="element-182">Nombres y apellidos</li>
                  <li data-id="element-183">Número de Cédula</li>
                  <li data-id="element-184">Correo electrónico</li>
                  <li data-id="element-185">Número de teléfono</li>
                  <li data-id="element-186">
                    Dirección IP y datos de navegación (cookies, ubicación, tipo
                    de navegador)
                  </li>
                  <li data-id="element-187">
                    Información proporcionada mediante formularios de contacto o
                    suscripción
                  </li>
                </ul>
              </section>

              <section data-id="element-188">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-189">
                  4. FINALIDAD DEL TRATAMIENTO
                </h3>
                <p className="mb-3" data-id="element-190">
                  Recopilamos tus datos con las siguientes finalidades:
                </p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-191">
                  <li data-id="element-192">
                    Enviar información institucional, boletines, eventos,
                    productos y servicios
                  </li>
                  <li data-id="element-193">
                    Atender consultas o solicitudes realizadas a través de
                    formularios de contacto
                  </li>
                  <li data-id="element-194">Gestionar inscripciones a eventos o programas</li>
                  <li data-id="element-195">
                    Gestionar promociones, beneficios, sistemas de fidelización
                    y otras actividades de marketing
                  </li>
                  <li data-id="element-196">
                    Realizar análisis estadísticos de navegación (uso de
                    cookies)
                  </li>
                  <li data-id="element-197">Cumplir con obligaciones legales y regulatorias</li>
                </ul>
              </section>

              <section data-id="element-198">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-199">
                  5. LEGITIMACIÓN PARA EL TRATAMIENTO
                </h3>
                <p className="mb-3" data-id="element-200">
                  El tratamiento de tus datos se realiza con base en:
                </p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-201">
                  <li data-id="element-202">Tu consentimiento expreso</li>
                  <li data-id="element-203">
                    El interés legítimo de TicketMaestro para cumplir su misión
                    institucional
                  </li>
                  <li data-id="element-204">
                    El cumplimiento de obligaciones legales, según la normativa
                    ecuatoriana aplicable (Ley Orgánica de Protección de Datos
                    Personales – LOPDP)
                  </li>
                </ul>
              </section>

              <section data-id="element-205">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-206">
                  6. DESTINATARIOS DE LOS DATOS
                </h3>
                <p className="mb-3" data-id="element-207">
                  Tus datos personales no serán compartidos con terceros, salvo
                  que:
                </p>
                <ul className="list-disc pl-5 space-y-1" data-id="element-208">
                  <li data-id="element-209">Tengamos tu consentimiento previo</li>
                  <li data-id="element-210">
                    Sea requerido por autoridad competente conforme a la ley
                  </li>
                  <li data-id="element-211">
                    Se usen herramientas tecnológicas de terceros (por ejemplo,
                    para envío de newsletters, CRM o formularios) con contratos
                    que garantizan la protección de tus datos
                  </li>
                </ul>
              </section>

              <section data-id="element-212">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-213">
                  7. DERECHOS DEL TITULAR DE LOS DATOS
                </h3>
                <p className="mb-3" data-id="element-214">Tienes derecho a:</p>
                <ul className="list-disc pl-5 space-y-1 mb-3" data-id="element-215">
                  <li data-id="element-216">Acceder a tus datos personales</li>
                  <li data-id="element-217">Rectificar datos inexactos o incompletos</li>
                  <li data-id="element-218">Eliminar tus datos (cuando proceda)</li>
                  <li data-id="element-219">Oponerte al tratamiento</li>
                  <li data-id="element-220">Solicitar la portabilidad de tus datos</li>
                  <li data-id="element-221">Revocar el consentimiento en cualquier momento</li>
                </ul>
                <p data-id="element-222">
                  Puedes ejercer estos derechos escribiéndonos a{' '}
                  <a href="mailto:protecciondatos@ticketmaestro.net" className="text-indigo-600 hover:underline" data-id="element-223">
                    protecciondatos@ticketmaestro.net
                  </a>
                </p>
              </section>

              <section data-id="element-224">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-225">
                  8. USO DE COOKIES
                </h3>
                <p className="leading-relaxed" data-id="element-226">
                  Nuestros sitios utilizan cookies propias y de terceros para
                  mejorar la experiencia de navegación y análisis del tráfico.
                  Puedes aceptar, configurar o rechazar el uso de cookies desde
                  el banner de consentimiento al ingresar al sitio.
                </p>
              </section>

              <section data-id="element-227">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-228">
                  9. SEGURIDAD DE LA INFORMACIÓN
                </h3>
                <p className="leading-relaxed" data-id="element-229">
                  TicketMaestro aplica medidas de seguridad técnicas y
                  organizativas adecuadas para proteger tus datos personales
                  contra accesos no autorizados, pérdida, destrucción o
                  alteración.
                </p>
              </section>

              <section data-id="element-230">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-231">
                  10. CONSERVACIÓN DE LOS DATOS
                </h3>
                <p className="leading-relaxed" data-id="element-232">
                  Tus datos serán conservados solo durante el tiempo necesario
                  para cumplir con la finalidad para la que fueron recopilados o
                  mientras exista una relación institucional o contractual.
                  Posteriormente serán eliminados o anonimizados, según
                  corresponda.
                </p>
              </section>

              <section data-id="element-233">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-234">
                  11. CAMBIOS A ESTA POLÍTICA
                </h3>
                <p className="leading-relaxed" data-id="element-235">
                  Nos reservamos el derecho de modificar esta Política de
                  Privacidad en cualquier momento. Cualquier cambio será
                  publicado con la fecha de actualización correspondiente.
                </p>
              </section>

              <section data-id="element-236">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-237">
                  12. AUTORIDAD DE CONTROL
                </h3>
                <p className="leading-relaxed" data-id="element-238">
                  Si consideras que tus derechos no han sido respetados, puedes
                  presentar un reclamo ante la Autoridad de Protección de Datos
                  Personales de Ecuador, la Superintendencia de Protección de
                  Datos Personales.
                </p>
              </section>

              <section data-id="element-239">
                <h3 className="text-lg font-bold text-slate-900 mb-3" data-id="element-240">
                  13. VIGENCIA
                </h3>
                <p className="leading-relaxed" data-id="element-241">
                  La vigencia de estas Políticas de Privacidad y Protección de
                  Datos es desde el 21 de noviembre de 2025 y permanecerá
                  vigente hasta que exista una nueva versión que la reemplace.
                </p>
              </section>
            </div>

            {/* Footer */}
            <div className="p-4 md:p-6 border-t border-slate-100 bg-slate-50 flex justify-end" data-id="element-242">
              <button onClick={onClose} className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm" data-id="element-243">
                Entendido
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}